import { EngineBlendType } from '@services/engine/types'
import { OverlayOptions } from '@spectrum-web-components/overlay'
import { ProjectState, StudioPanel } from '@store/slices/projectSlice'
import { RichTooltipProps } from './RichTooltip'

export type StudioUISizePreference = ProjectState['sizePreference']
export type RichTooltipUsageContext = string
export type RichTooltipOffsetPx = number

export const ToolbarTooltipOffsetWhenAddObjectRailIsOpen: RichTooltipOffsetPx = 82

export type StudioRichTooltipProps = Omit<RichTooltipProps, 'selfManaged'> & {
  offsetPxByUISize?: {
    [key in StudioUISizePreference]: RichTooltipOffsetPx
  }
  overlayWrapperConfig: Pick<OverlayOptions, 'offset'> & {
    // ex: 'toggle-orbit-constraints-button@hover'
    trigger: string
  }
}

export const StudioRichTooltipPresets: {
  [location: RichTooltipUsageContext]: StudioRichTooltipProps & {
    key: React.Key
  }
} = {
  alignTool: {
    placement: 'right-start',
    description: 'studio:richTooltip:alignObjects:description',
    key: 'rich-tooltip-align-tool',
    keybind: 'studio:richTooltip:alignObjects:keybind',
    title: 'studio:richTooltip:alignObjects',
    videoSources: [
      { src: '/media/rich_tooltips/_Align_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Align_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'toolbar-button-align3DTool@hover'
    }
  },
  blendTypeAdd: {
    placement: 'left',
    description: 'studio:richTooltip:add:description',
    key: 'rich-tooltip-add-blend',
    offsetPx: 32,
    title: 'studio:richTooltip:add',
    videoSources: [
      { src: '/media/rich_tooltips/_Combine_Add_Retime_1.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Combine_Add_Retime_1.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'blend-type-button-add@hover'
    }
  },
  blendTypeAvoid: {
    placement: 'left',
    description: 'studio:richTooltip:avoid:description',
    key: 'rich-tooltip-avoid-blend',
    offsetPxByUISize: {
      s: 160,
      m: 202.5,
      l: 233
    },
    title: 'studio:richTooltip:avoid',
    videoSources: [
      { src: '/media/rich_tooltips/_Combine_Avoid_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Combine_Avoid_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'blend-type-button-avoid@hover'
    }
  },
  blendTypeCarve: {
    placement: 'left',
    description: 'studio:richTooltip:carve:description',
    key: 'rich-tooltip-carve-blend',
    offsetPx: 32,
    title: 'studio:richTooltip:carve',
    videoSources: [
      { src: '/media/rich_tooltips/_Combine_Carve_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Combine_Carve_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'blend-type-button-carve@hover'
    }
  },
  blendTypeColor: {
    placement: 'left',
    description: 'studio:richTooltip:color:description',
    key: 'rich-tooltip-color-blend',
    offsetPxByUISize: {
      s: 70,
      m: 83,
      l: 92.5
    },
    title: 'studio:richTooltip:color',
    videoSources: [
      { src: '/media/rich_tooltips/_Combine_Color_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Combine_Color_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'blend-type-button-color@hover'
    }
  },
  blendTypeIntersect: {
    placement: 'left',
    description: 'studio:richTooltip:intersect:description',
    key: 'rich-tooltip-intersect-blend',
    offsetPxByUISize: {
      s: 78.5,
      m: 93.5,
      l: 104
    },
    title: 'studio:richTooltip:intersect',
    videoSources: [
      {
        src: '/media/rich_tooltips/_Combine_Intersect_Retime_2.webm',
        type: 'webm'
      },
      {
        src: '/media/rich_tooltips/_Combine_Intersect_Retime_2.mp4',
        type: 'mp4'
      }
    ],
    overlayWrapperConfig: {
      trigger: 'blend-type-button-intersect@hover'
    }
  },
  blendTypeRepel: {
    placement: 'left',
    description: 'studio:richTooltip:repel:description',
    key: 'rich-tooltip-repel-blend',
    offsetPxByUISize: {
      s: 114,
      m: 142,
      l: 161.5
    },
    title: 'studio:richTooltip:repel',
    videoSources: [
      { src: '/media/rich_tooltips/_Combine_Repel_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Combine_Repel_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'blend-type-button-repel@hover'
    }
  },
  blendingSlider: {
    placement: 'left',
    description: 'studio:richTooltip:blending:description',
    key: 'rich-tooltip-blending-slider',
    offsetPx: 32,
    title: 'studio:richTooltip:blending',
    videoSources: [],
    overlayWrapperConfig: {
      trigger: 'blending-slider-container@hover'
    }
  },
  editShapeTool: {
    placement: 'right-start',
    description: 'studio:richTooltip:editShape:description',
    key: 'rich-tooltip-edit-shape-tool',
    keybind: 'studio:richTooltip:editShape:keybind',
    title: 'studio:richTooltip:editShape',
    videoSources: [
      { src: '/media/rich_tooltips/_ShapeEdit_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_ShapeEdit_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'toolbar-button-editShape@hover'
    }
  },
  environmentActionMenu: {
    placement: 'bottom-start',
    description: 'studio:richTooltip:environment:description',
    key: 'rich-tooltip-environment-action-menu',
    offsetPx: 19,
    title: 'studio:richTooltip:environment',
    videoSources: [
      { src: '/media/rich_tooltips/_Env_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Env_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'environment-action-menu-popover-trigger@hover'
    }
  },
  generate: {
    placement: 'left-start',
    delayed: false,
    description: 'studio:richTooltip:generate:description',
    key: 'rich-tooltip-generate-tool',
    title: 'studio:richTooltip:generate',
    videoSources: [
      { src: '/media/rich_tooltips/_Generate.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Generate.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: `right-rail-action-button-${StudioPanel.Firefly}@hover`
    }
  },
  horizontalOrbitSlider: {
    placement: 'bottom',
    description: 'studio:richTooltip:orbit:description',
    key: 'rich-tooltip-horizontal-orbit-slider',
    offsetPx: 16.5,
    title: 'studio:richTooltip:orbit',
    videoSources: [
      { src: '/media/rich_tooltips/_Camera2_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Camera2_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'horizontal-orbit-slider@hover'
    }
  },
  horizontalOrbitSliderLeft: {
    placement: 'bottom',
    description: 'studio:richTooltip:orbit:left:description',
    key: 'rich-tooltip-horizontal-orbit-slider-left',
    keybind: 'studio:richTooltip:orbit:left:keybind',
    offsetPx: 16.5,
    title: 'studio:richTooltip:orbit:left',
    videoSources: [
      { src: '/media/rich_tooltips/_Camera2_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Camera2_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      offset: [0, 56.5],
      trigger: 'horizontal-orbit-slider-left-button@hover'
    }
  },
  horizontalOrbitSliderRight: {
    placement: 'bottom',
    description: 'studio:richTooltip:orbit:right:description',
    key: 'rich-tooltip-horizontal-orbit-slider-right',
    keybind: 'studio:richTooltip:orbit:right:keybind',
    offsetPx: 16.5,
    title: 'studio:richTooltip:orbit:right',
    videoSources: [
      { src: '/media/rich_tooltips/_Camera2_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Camera2_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      offset: [0, -56.5],
      trigger: 'horizontal-orbit-slider-right-button@hover'
    }
  },
  selectTool: {
    placement: 'right-start',
    description: 'studio:richTooltip:select:description',
    key: 'rich-tooltip-select-tool',
    keybind: 'studio:richTooltip:select:keybind',
    title: 'studio:richTooltip:select',
    videoSources: [
      { src: '/media/rich_tooltips/_Select1_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Select1_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'toolbar-button-select@hover'
    }
  },
  shapeTool: {
    placement: 'right-start',
    description: 'studio:richTooltip:shapes:description',
    key: 'rich-tooltip-shape-tool',
    title: 'studio:richTooltip:shapes',
    videoSources: [
      { src: '/media/rich_tooltips/_Shapes_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Shapes_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'three-d-shape-menu-trigger@hover'
    }
  },
  snapView: {
    placement: 'bottom',
    description: 'studio:richTooltip:snapView:description',
    key: 'rich-tooltip-snap-view',
    offsetPx: 16.5,
    title: 'studio:richTooltip:snapView',
    videoSources: [
      { src: '/media/rich_tooltips/_SnapView3.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_SnapView3.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'toggle-orbit-constraints-button@hover'
    }
  },
  stylesActionMenu: {
    placement: 'bottom-start',
    description: 'studio:richTooltip:styles:description',
    key: 'rich-tooltip-styles-action-menu',
    offsetPx: 19,
    title: 'studio:richTooltip:styles',
    videoSources: [
      { src: '/media/rich_tooltips/_Styles_1.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Styles_1.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      offset: [0, -3],
      trigger: 'styles-action-menu-popover-trigger@hover'
    }
  },
  verticalOrbitSlider: {
    placement: 'right',
    description: 'studio:richTooltip:tumble:description',
    key: 'rich-tooltip-vertical-orbit-slider',
    title: 'studio:richTooltip:tumble',
    videoSources: [
      { src: '/media/rich_tooltips/_Camera2_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Camera2_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      trigger: 'vertical-orbit-slider@hover'
    }
  },
  verticalOrbitSliderDown: {
    placement: 'right',
    description: 'studio:richTooltip:tumble:down:description',
    key: 'rich-tooltip-vertical-orbit-slider-down',
    keybind: 'studio:richTooltip:tumble:down:keybind',
    title: 'studio:richTooltip:tumble:down',
    videoSources: [
      { src: '/media/rich_tooltips/_Camera2_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Camera2_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      offset: [0, -56.5],
      trigger: 'vertical-orbit-slider-down-button@hover'
    }
  },
  verticalOrbitSliderUp: {
    placement: 'right',
    description: 'studio:richTooltip:tumble:up:description',
    key: 'rich-tooltip-vertical-orbit-slider-up',
    keybind: 'studio:richTooltip:tumble:up:keybind',
    title: 'studio:richTooltip:tumble:up',
    videoSources: [
      { src: '/media/rich_tooltips/_Camera2_Retime.webm', type: 'webm' },
      { src: '/media/rich_tooltips/_Camera2_Retime.mp4', type: 'mp4' }
    ],
    overlayWrapperConfig: {
      offset: [0, 56.5],
      trigger: 'vertical-orbit-slider-up-button@hover'
    }
  }
}

export const getBlendingSliderVideoSources = (
  selectedBlendType: EngineBlendType
): RichTooltipProps['videoSources'] => {
  switch (selectedBlendType) {
    case EngineBlendType.ADD:
      return StudioRichTooltipPresets.blendTypeAdd.videoSources
    case EngineBlendType.COLOR:
      return StudioRichTooltipPresets.blendTypeColor.videoSources
    case EngineBlendType.REPEL:
      return StudioRichTooltipPresets.blendTypeRepel.videoSources
    case EngineBlendType.AVOID:
      return StudioRichTooltipPresets.blendTypeAvoid.videoSources
    case EngineBlendType.SUBTRACT:
      return StudioRichTooltipPresets.blendTypeCarve.videoSources
    case EngineBlendType.INTERSECT:
      return StudioRichTooltipPresets.blendTypeIntersect.videoSources
    default:
      return StudioRichTooltipPresets.blendTypeAdd.videoSources
  }
}
